import { Vehicle } from './Vehicle';
import { LevelUtil } from './level/levels/LevelUtil';

export interface PlayerControls {
  changeLaneUp: Phaser.Input.Keyboard.Key;
  changeLaneUpAlt: Phaser.Input.Keyboard.Key;
  changeLaneDown: Phaser.Input.Keyboard.Key;
  changeLaneDownAlt: Phaser.Input.Keyboard.Key;
  accelerate: Phaser.Input.Keyboard.Key;
  accelerateAlt: Phaser.Input.Keyboard.Key;
}

export interface DebugPlayerControls {
  toggleDebugMovement: Phaser.Input.Keyboard.Key;
  // up: Phaser.Input.Keyboard.Key;
  // upSecond: Phaser.Input.Keyboard.Key;
  // down: Phaser.Input.Keyboard.Key;
  // downSecond: Phaser.Input.Keyboard.Key;
  left: Phaser.Input.Keyboard.Key;
  leftSecond: Phaser.Input.Keyboard.Key;
  right: Phaser.Input.Keyboard.Key;
  rightSecond: Phaser.Input.Keyboard.Key;
  jumpReset: Phaser.Input.Keyboard.Key;
  autoRun: Phaser.Input.Keyboard.Key;
}

export class PlayerController {
  public static events = {} as const;

  protected scene: Phaser.Scene;
  protected player: Vehicle;

  protected playerControls: PlayerControls;
  protected debugControls: DebugPlayerControls;

  protected allowDebugControls: boolean;
  protected allowDebugMovement: boolean;

  protected isLaneChangingUp: boolean;
  protected isLaneChangingDown: boolean;

  protected isPointerDown: boolean;
  protected laneBoundaries: number[];
  protected pointerTargetLane: number;

  constructor(scene: Phaser.Scene, player: Vehicle) {
    this.scene = scene;
    this.player = player;

    this.playerControls = this.scene.input.keyboard.addKeys({
      changeLaneUp: Phaser.Input.Keyboard.KeyCodes.W,
      changeLaneUpAlt: Phaser.Input.Keyboard.KeyCodes.UP,
      changeLaneDown: Phaser.Input.Keyboard.KeyCodes.S,
      changeLaneDownAlt: Phaser.Input.Keyboard.KeyCodes.DOWN,
      accelerate: Phaser.Input.Keyboard.KeyCodes.SPACE,
      accelerateAlt: Phaser.Input.Keyboard.KeyCodes.RIGHT
    }) as PlayerControls;

    this.debugControls = this.scene.input.keyboard.addKeys({
      toggleDebugMovement: Phaser.Input.Keyboard.KeyCodes.T,
      // up: Phaser.Input.Keyboard.KeyCodes.W,
      // upSecond: Phaser.Input.Keyboard.KeyCodes.UP,
      // down: Phaser.Input.Keyboard.KeyCodes.S,
      // downSecond: Phaser.Input.Keyboard.KeyCodes.DOWN,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      leftSecond: Phaser.Input.Keyboard.KeyCodes.LEFT,
      right: Phaser.Input.Keyboard.KeyCodes.D,
      rightSecond: Phaser.Input.Keyboard.KeyCodes.RIGHT,
      autoRun: Phaser.Input.Keyboard.KeyCodes.Q
    }) as DebugPlayerControls;

    this.allowDebugControls = !__IS_PRODUCTION__;
    this.allowDebugMovement = false;

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);

    this.scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown, this);
    this.scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove, this);
    this.scene.input.on(Phaser.Input.Events.POINTER_UP, this.onPointerUp, this);

    this.playerControls.accelerate.on(Phaser.Input.Keyboard.Events.DOWN, this.accelerateDown, this);
    this.playerControls.accelerate.on(Phaser.Input.Keyboard.Events.UP, this.accelerateUp, this);
    this.playerControls.accelerateAlt.on(Phaser.Input.Keyboard.Events.DOWN, this.accelerateDown, this);
    this.playerControls.accelerateAlt.on(Phaser.Input.Keyboard.Events.UP, this.accelerateUp, this);
    this.playerControls.changeLaneUpAlt.on(Phaser.Input.Keyboard.Events.DOWN, this.upKeyDown, this);
    this.playerControls.changeLaneUpAlt.on(Phaser.Input.Keyboard.Events.UP, this.upKeyUp, this);
    this.playerControls.changeLaneDownAlt.on(Phaser.Input.Keyboard.Events.DOWN, this.downKeyDown, this);
    this.playerControls.changeLaneDownAlt.on(Phaser.Input.Keyboard.Events.UP, this.downKeyUp, this);

    this.player.on(Phaser.GameObjects.Events.DESTROY, () => {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);

      this.scene.input.off(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown, this);
      this.scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove, this);
      this.scene.input.off(Phaser.Input.Events.POINTER_UP, this.onPointerUp, this);

      this.playerControls.accelerate.off(Phaser.Input.Keyboard.Events.DOWN, this.accelerateDown, this);
      this.playerControls.accelerate.off(Phaser.Input.Keyboard.Events.UP, this.accelerateUp, this);
      this.playerControls.accelerateAlt.off(Phaser.Input.Keyboard.Events.DOWN, this.accelerateDown, this);
      this.playerControls.accelerateAlt.off(Phaser.Input.Keyboard.Events.UP, this.accelerateUp, this);
      this.playerControls.changeLaneUpAlt.off(Phaser.Input.Keyboard.Events.DOWN, this.upKeyDown, this);
      this.playerControls.changeLaneUpAlt.off(Phaser.Input.Keyboard.Events.UP, this.upKeyUp, this);
      this.playerControls.changeLaneDownAlt.off(Phaser.Input.Keyboard.Events.DOWN, this.downKeyDown, this);
      this.playerControls.changeLaneDownAlt.off(Phaser.Input.Keyboard.Events.UP, this.downKeyUp, this);
    });

    this.isLaneChangingUp = false;
    this.isLaneChangingDown = false;

    this.isPointerDown = false;
    this.laneBoundaries = LevelUtil.laneBoundaries.slice();
    // We don't need the top boundary of lane 0 or the bottom boundary of lane 3.
    this.laneBoundaries.splice(0, 1);
    this.laneBoundaries.splice(this.laneBoundaries.length - 1, 1);
    this.pointerTargetLane = 0;
  }

  private accelerateDown() {
    this.player.accelerate();
  }

  private accelerateUp() {
    this.player.decelerate();
  }

  private upKeyDown() {
    this.player.isLaneChangingUp = true;
    this.isLaneChangingUp = true;
  }

  private upKeyUp() {
    this.player.isLaneChangingUp = false;
    this.isLaneChangingUp = false;
  }

  private downKeyDown() {
    this.player.isLaneChangingDown = true;
    this.isLaneChangingDown = true;
  }

  private downKeyUp() {
    this.player.isLaneChangingDown = false;
    this.isLaneChangingDown = false;
  }

  public update(time: number, delta: number) {
    if (this.pointerTargetLane >= 1) {
      if (this.player.changeLaneUp()) this.pointerTargetLane--;
    } else if (this.pointerTargetLane <= -1) {
      if (this.player.changeLaneDown()) this.pointerTargetLane++;
    }

    // Will be blocked if already changing lane.
    if (this.isLaneChangingUp) {
      this.player.changeLaneUp();
    } else if (this.isLaneChangingDown) {
      this.player.changeLaneDown();
    }

    /*if(Phaser.Input.Keyboard.JustDown(this.playerControls.accelerate)){
      this.player.isAccelerating = true;
    }
    else if(Phaser.Input.Keyboard.JustUp(this.playerControls.accelerate)){
      this.player.isAccelerating = false;
    }*/

    if (Phaser.Input.Keyboard.JustDown(this.playerControls.changeLaneUp)) {
      this.player.changeLaneUp();
      this.isLaneChangingUp = true;
    } else if (Phaser.Input.Keyboard.JustUp(this.playerControls.changeLaneUp)) {
      this.isLaneChangingUp = false;
    }

    if (Phaser.Input.Keyboard.JustDown(this.playerControls.changeLaneDown)) {
      this.player.changeLaneDown();
      this.isLaneChangingDown = true;
    }
    if (Phaser.Input.Keyboard.JustUp(this.playerControls.changeLaneDown)) {
      this.isLaneChangingDown = false;
    }

    if (this.allowDebugControls) {
      if (Phaser.Input.Keyboard.JustDown(this.debugControls.autoRun)) {
        if (this.player.IsMoving) {
          this.player.stopMoving();
        } else {
          this.player.startMoving();
        }
      }

      if (Phaser.Input.Keyboard.JustUp(this.debugControls.toggleDebugMovement)) {
        this.allowDebugMovement = !this.allowDebugMovement;
        // (this.player.body as MatterJS.BodyType).ignoreGravity = this.allowDebugMovement;
      }

      if (this.allowDebugMovement) {
        // if (this.debugControls.up.isDown || this.debugControls.upSecond.isDown) {
        //   this.player.y -= 1 * delta;
        // }
        // if (this.debugControls.down.isDown || this.debugControls.downSecond.isDown) {
        //   this.player.y += 1 * delta;
        // }

        if (this.debugControls.right.isDown || this.debugControls.rightSecond.isDown) {
          this.player.x += 1 * delta;
        }

        if (this.debugControls.left.isDown || this.debugControls.leftSecond.isDown) {
          this.player.x -= 1 * delta;
        }
      }
    }
  }

  protected onPointerDown(pointer: Phaser.Input.Pointer) {
    this.isPointerDown = true;
    this.player.accelerate();
    this.getTargetLane(pointer.y);
  }

  protected onPointerMove(pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]) {
    if (!this.isPointerDown) return;

    this.getTargetLane(pointer.y);
  }

  protected onPointerUp() {
    this.player.decelerate();
    this.isPointerDown = false;
    this.pointerTargetLane = 0;
  }

  protected getTargetLane(y: number) {
    // Pointer origin top left and lane boundaries is centre.
    let offsetY = y - this.scene.cameras.main.displayHeight * 0.5;

    // console.log('Current Pointer Lane', this.getLaneFromYPosition(offsetY, this.laneBoundaries));
    let targetLane = this.getLaneFromYPosition(offsetY, this.laneBoundaries);
    let diff = this.player.CurrentLane - targetLane;

    this.pointerTargetLane = diff;
  }

  protected getLaneFromYPosition(y: number, laneBoundaries: number[]) {
    for (let i = 0; i < this.player.RoadDetails.laneCount; i++) {
      if (y < laneBoundaries[i]) return i;
    }

    // If past last boundary and not above we must be below.
    return this.player.RoadDetails.laneCount - 1;
  }
}
